/* eslint-disable @typescript-eslint/no-magic-numbers */
import { lighten, getContrast, parseToHsl, setSaturation, shade, darken, tint } from 'polished';
import {
  adjustColorForProperContrast,
  colorContrastRatiosByShape,
  isDarkColor,
} from '~/components/AccessibleColorPicker/color-palette-logic';
import { DARK_COLOR_WITH_HEX, WHITE_WITH_HEX } from './utilities';

export const getSlightAccentedBackgroundColor = (
  backgroundColor: string,
  isEventRoom = false,
): string => {
  if (backgroundColor === WHITE_WITH_HEX && !isEventRoom) {
    return WHITE_WITH_HEX;
  }

  if (isDarkColor(backgroundColor)) {
    return tint(0.08, backgroundColor);
  }
  return darken(0.05, backgroundColor);
};

export const getHeavyAccentedBackgroundColor = (backgroundColor: string): string => {
  if (isDarkColor(backgroundColor)) {
    return tint(0.3, backgroundColor);
  }
  return darken(0.15, backgroundColor);
};

export const getWhiteOrBlack = (backgroundColor: string): string => {
  if (isDarkColor(backgroundColor)) {
    return WHITE_WITH_HEX;
  }
  return '#000000';
};

export const getBrandLightBackgroundColor = (primaryColor: string): string => {
  const { saturation: primaryColorSaturation, lightness: primaryColorLightness } =
    parseToHsl(primaryColor);
  let updatedSaturation = Math.max(0.05, primaryColorSaturation);
  if (primaryColorLightness < 0.05 || primaryColorLightness > 0.95) {
    updatedSaturation = 0;
  }
  // Nake the current color *almost* white and then darken it just a bit
  // to get a nice looking light background color based on the primary color
  let newBackgroundColor = setSaturation(updatedSaturation, shade(0.02, tint(0.95, primaryColor)));

  // If a very light color was selected, a light version of it won't provide enough contrast to
  // be used as the background color.
  // In these cases, we'll darken the primary color a bit to get a "light" color background
  // that still has sufficient contrast.
  if (
    getContrast(primaryColor, newBackgroundColor) <
    colorContrastRatiosByShape.backgroundColorWhereNonTextHitAreaContentsHaveProperContrast
  ) {
    newBackgroundColor = setSaturation(
      updatedSaturation,
      adjustColorForProperContrast(
        newBackgroundColor,
        primaryColor,
        'backgroundColorWhereNonTextHitAreaContentsHaveProperContrast',
      ),
    );
    // Make sure we still update the saturation of the new background color to tone it down
    newBackgroundColor = setSaturation(updatedSaturation, newBackgroundColor);
  }
  return newBackgroundColor;
};

export const getBrandDarkBackgroundColor = (primaryColor: string): string => {
  const { saturation: primaryColorSaturation, lightness: primaryColorLightness } =
    parseToHsl(primaryColor);
  let updatedSaturation = Math.min(0.3, primaryColorSaturation);
  // If the new color is close to black or white, zero out the saturation so
  // we end up with gray instead of pink
  if (primaryColorLightness < 0.05 || primaryColorLightness > 0.95) {
    updatedSaturation = 0;
  }
  // Nake the current color *almost* black and then lighten it just a bit
  // to get a nice looking dark background color based on the primary color
  let newBackgroundColor = setSaturation(
    updatedSaturation,
    lighten(0.15, shade(0.95, primaryColor)),
  );
  // If a very dark color was selected, a dark version of it won't provide enough contrast to
  // be used as the background color.
  // In these cases, we'll lighten the primary color a bit to get a "dark" color background
  // that still has sufficient contrast.
  if (
    getContrast(primaryColor, newBackgroundColor) <
    colorContrastRatiosByShape.backgroundColorWhereNonTextHitAreaContentsHaveProperContrast
  ) {
    newBackgroundColor = adjustColorForProperContrast(
      newBackgroundColor,
      primaryColor,
      'backgroundColorWhereNonTextHitAreaContentsHaveProperContrast',
    );
    // Make sure we still update the saturation of the new background color to tone it down
    newBackgroundColor = setSaturation(updatedSaturation, newBackgroundColor);
  }

  return newBackgroundColor;
};

export const getColorsToExcludeFromText = (colors: string[]): string[] => {
  return colors.filter(
    (color) =>
      color.toLowerCase() !== DARK_COLOR_WITH_HEX &&
      color.toLowerCase() !== WHITE_WITH_HEX &&
      color.toLowerCase() !== '#000000',
  );
};
